// @flow
import React, { Component } from 'react';
import { Link } from 'react-router';
import entries from 'object.entries';

import inBrowser from '../../utils/inBrowser';
import type { API } from '../../reducers/version';
import MobileDropdown from '../MobileDropdown';

import style from './style.css';
import logo from './logo.svg';

type Props = {
  api: API;
  index: Object;
  pinNav: Function;
  unpinNav: Function;
  navPinned: boolean;
  version: string;
  showPrivateAPI: boolean;
  pathname: string;
};

type State = {
  activeMenu: ?('api' | 'guide');
  activeMobile: ?('api' | 'guide');
};

const DESKTOP_WIDTH = 960;

function getScrollY() {
  if (!inBrowser()) {
    return 0;
  }

  return document.documentElement.scrollTop || document.body.scrollTop;
}

function getWindowWidth() {
  return inBrowser() ? window.innerWidth : 0;
}

function createScrollHandler(component) {
  let prevOffset;

  return () => {
    if (getWindowWidth() >= DESKTOP_WIDTH) {
      return;
    }

    const {
      props: {
        pinNav,
        unpinNav,
        navPinned
      },
      element: {
        offsetTop
      }
    } = component;
    const scrollY = getScrollY();

    if (!navPinned && scrollY >= offsetTop) {
      prevOffset = offsetTop;
      pinNav();
    } else if (navPinned && scrollY < prevOffset) {
      unpinNav();
    }
  };
}

class Sidebar extends Component<void, Props, State> {
  element: HTMLElement;
  handleResize: () => void;
  handleScroll: () => void;

  state: State = {
    activeMenu: 'guide',
    activeMobile: null,
  };

  componentDidMount() {
    if (inBrowser()) {
      const handleScroll = createScrollHandler(this);
      const handleResize = () => {
        if (getWindowWidth() < DESKTOP_WIDTH) {
          handleScroll();
        }
      };

      this.handleResize = handleResize;
      this.handleScroll = handleScroll;

      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll);
    }
  }

  componentWillUnmount() {
    if (inBrowser()) {
      window.removeEventListener('resize', this.handleResize);
      window.removeEventListener('scroll', this.handleScroll);
    }
  }

  showGuide = (evt: Event & { which?: number }) => {
    const notKeypress = typeof evt.which === 'undefined';
    const validKeys = evt.which === 32 || evt.which === 13;
    if (notKeypress || validKeys) {
      this.setState({
        activeMenu: 'guide',
      });
      if (inBrowser()) {
        const tabletRange = window.innerWidth >= 640 && window.innerWidth < 960;
        if (tabletRange) {
          if (this.state.activeMobile !== 'guide') {
            this.setState({
              activeMobile: 'guide'
            });
          } else {
            this.hideMenu();
          }
        }
      }
    }
  };

  showAPI = (evt: Event & { which?: number }) => {
    const notKeypress = typeof evt.which === 'undefined';
    const validKeys = evt.which === 32 || evt.which === 13;
    if (notKeypress || validKeys) {
      this.setState({
        activeMenu: 'api',
      });
      if (inBrowser()) {
        const tabletRange = window.innerWidth >= 640 && window.innerWidth < 960;
        if (tabletRange) {
          if (this.state.activeMobile !== 'api') {
            this.setState({
              activeMobile: 'api'
            });
          } else {
            this.hideMenu();
          }
        }
      }
    }
  };

  hideMenu = () => {
    this.setState({
      activeMobile: null
    });
  };

  render() {
    const {
      props: {
        api,
        index,
        pathname,
        showPrivateAPI,
        version,
      }
    } = this;

    const { activeMenu, activeMobile } = this.state;

    const ref = el => {
      this.element = el;
    };

    let activeClassMethods = () => null;
    let activeClassProperties = () => null;

    if (api) {
      let { methods, properties } = api;

      if (!showPrivateAPI) {
        methods = methods.filter(({ isPrivate }) => (
          !isPrivate
        ));

        properties = properties.filter(({ isPrivate }) => (
          !isPrivate
        ));
      }

      activeClassMethods = name => {
        if (name === api.name) {
          return (
            <ul className={`list pa0 ${style.subOptions}`}>
              {methods.map(method => (
                <li
                  className={style.subkey}
                  key={method.slug}
                >
                  <a className="db" href={`#${method.slug}`}>
                    {`${method.isStatic ? '.' : '#'}${method.name}()`}
                  </a>
                </li>
              ))}
            </ul>
          );
        }

        return null;
      };

      activeClassProperties = name => {
        if (name === api.name) {
          return (
            <ul className={`list pa0 ${style.subOptions}`}>
              {properties.map(prop => (
                <li
                  className={style.subkey}
                  key={prop.slug}
                >
                  <a className="db" href={`#${prop.slug}`}>
                    {`${prop.isStatic ? '.' : '#'}${prop.name}`}
                  </a>
                </li>
              ))}
            </ul>
          );
        }

        return null;
      };
    }

    return (
      <aside className={style.sidebar} ref={ref}>
        <div className={`w-100 ma0-l ${style.sidebarContents}`}>
          <Link className={`db relative static-l ${style.logo}`} to="/">
            <img alt="Lux" src={logo} />
          </Link>
          <ul
            className={`list w-100-l ma0 pa0 ${style.optionsList}`}
          >
            <li
              className={`mr4 mr0-l ${
                activeMenu === 'guide' ? style.active : ''
              } ${style.mainOptionContainer} ${
                activeMobile === 'guide' ? style.activeMobile : ''
              }`}
              onMouseLeave={this.hideMenu}
            >
              <div className={style.menuMainLink}>
                <Link
                  activeClassName={style.active}
                  className="db pv2 pv3-l"
                  onClick={this.showGuide}
                  to={`/docs/${version}/guide`}
                >
                  Guide
                </Link>
                <button
                  aria-label="Show or hide additional guide pages"
                  className={`${style.handle} handle`}
                  onClick={this.showGuide}
                  onKeyPress={this.showGuide}
                >
                  <i />
                </button>
              </div>
              <ul
                className={`${style.options} list pa0 bg-medium`}
              >
                {entries(index.guide).map(([slug, title]) => (
                  <li
                    className={`${style.pageKey} ${
                      pathname.match(`/${slug}`) ? style.active : ''}`}
                    key={slug}
                  >
                    <Link
                      to={`/docs/${version}/guide/${slug}`}
                    >
                      {title}
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
            <li
              className={` ${
                activeMenu === 'api' ? style.active : ''
              } ${style.mainOptionContainer} ${
                activeMobile === 'api' ? style.activeMobile : ''
              }`}
              onMouseLeave={this.hideMenu}
            >
              <div className={style.menuMainLink}>
                <Link
                  activeClassName={style.active}
                  className="db pv2 pv3-l"
                  onClick={this.showAPI}
                  to={`/docs/${version}/api`}
                >
                  API Reference
                </Link>
                <button
                  aria-label="Show or hide additional API pages"
                  className={`${style.handle} handle`}
                  onClick={this.showAPI}
                  onKeyPress={this.showAPI}
                >
                  <i />
                </button>
              </div>
              <ul
                className={`${style.options} list pa0 bg-medium`}
              >
                {entries(index.api).map(([slug, { name }]) => (
                  <li
                    className={`${style.pageKey} ${
                      pathname.match(`/${slug}`) ? style.active : ''}`}
                    key={slug}
                  >
                    <Link
                      activeClassName={style.active}
                      to={`/docs/${version}/api/${slug}`}
                    >
                      {name}
                    </Link>
                    {activeClassProperties(name)}
                    {activeClassMethods(name)}
                  </li>
                ))}
              </ul>
            </li>
          </ul>
        </div>
        <MobileDropdown
          index={index}
          pathname={pathname}
          version={version}
        />
      </aside>
    );
  }
}

export default Sidebar;



// WEBPACK FOOTER //
// ./src/components/Sidebar/index.jsx